<template>
    <div :class="cssClass" v-show="visible">
        <cogi-label :required="required" :label="label" />
        <div class="grid">
            <select class="gridspan5" v-model="yearString" :class="{ cogirequired: required, 'cogi-error': hasError }" @blur="blur" @change="resetDayValue">
                <option v-for="y in years" :value="y" :key="y">{{ y }}</option>
            </select>
            <select class="gridspan6" v-model="monthString" :class="{ cogirequired: required, 'cogi-error': hasError }" @blur="blur" @change="resetDayValue">
                <option v-for="m in months" :value="m.id" :key="m.id">{{ m.text }}</option>
            </select>
            <select class="gridspan5" v-model="dayString" :class="{ cogirequired: required, 'cogi-error': hasError }" @blur="blur">
                <option v-for="d in days" :value="d" :key="d">{{ d }}</option>
            </select>
        </div>
    </div>
</template>
<script setup lang="ts">
    import { ref, watch, computed } from 'vue'
    import CogiLabel from './CogiLabel.vue'

    interface IPropsDate {
        dateReassignee?: Date
        label?: string
        required?: boolean
        valideNow?: boolean
        cssClass?: string
        visible?: boolean
        culture?: string // 'fr' | 'en'
    }

    const props = withDefaults(defineProps<IPropsDate>(), {
        label: '',
        required: false,
        valideNow: false,
        cssClass: '',
        visible: true,
        culture: 'fr',
    })

    const model = defineModel<Date | undefined>({ required: true })
    const year = ref<number | undefined>(props.dateReassignee?.getFullYear() ?? model.value?.getFullYear())
    const month = ref<number | undefined>(props.dateReassignee?.getMonth() ?? model.value?.getMonth())
    const day = ref<number | undefined>(props.dateReassignee?.getDate() ?? model.value?.getDate())
    const hasError = ref(false)

    const emit = defineEmits<{
        validated: [boolean]
    }>()

    function validate(): void {
        hasError.value = false

        if (!props.visible) {
            emit('validated', false)
            return
        }

        if (props.required && (year.value == undefined || month.value == undefined || day.value == undefined)) hasError.value = true

        emit('validated', hasError.value)
    }

    function blur() {
        hasError.value = false
    }

    function resetDayValue() {
        day.value = undefined
    }

    watch(
        () => props.valideNow,
        (newValue: boolean) => {
            if (newValue) validate()
        },
    )

    watch(year, updateModel)
    watch(month, updateModel)
    watch(day, updateModel)

    watch(
        () => model.value,
        newValue => {
            if (newValue?.getFullYear() ?? 0 > 1900) {
                year.value = newValue?.getFullYear()
                month.value = newValue?.getMonth()
                day.value = newValue?.getDate()
            }
        },
    )

    const yearString = computed({
        get() {
            return year.value?.toString() ?? ''
        },
        set(newValue) {
            year.value = parseInt(newValue)
        },
    })
    const monthString = computed({
        get() {
            return month.value?.toString() ?? ''
        },
        set(newValue) {
            month.value = parseInt(newValue)
        },
    })
    const dayString = computed({
        get() {
            return day.value?.toString() ?? ''
        },
        set(newValue) {
            day.value = parseInt(newValue)
        },
    })

    const years = computed(() => {
        const yearArray = ['']
        const currentYear = new Date().getFullYear()
        for (let i = currentYear + 1; i > 1900; i--) {
            yearArray.push(i.toString())
        }

        return yearArray
    })

    const months = computed(() => {
        const locale = props.culture == 'en' ? 'en' : 'fr-ca'
        const monthArray = [{ id: '', text: '' }]

        for (let i = 0; i < 12; i++) {
            monthArray.push({
                id: i.toString(),
                text: new Date(2000, i, 1).toLocaleString(locale, { month: 'long' }),
            })
        }

        return monthArray
    })

    const days = computed(() => {
        const numberOfDays = new Date(year.value ?? NaN, (month.value ?? NaN) + 1, 0).getDate()

        const daysArray = ['']
        for (let i = 1; i <= numberOfDays; i++) {
            daysArray.push(i.toString())
        }

        return daysArray
    })

    function updateModel() {
        if (year.value == undefined || month.value == undefined || day.value == undefined) {
            model.value = undefined
        } else {
            model.value = new Date(year.value, month.value, day.value)
        }
    }
</script>
